/**
 * @fileoverview carousel-reel web component powered by Embla Carousel.
 */

// Import Embla Carousel ES module v8.6.0
import EmblaCarousel from './embla-carousel.esm.js';

const DESKTOP_BREAKPOINT = 964;
const LOAD_MORE_THRESHOLD = 10;
const DRAG_CLICK_THRESHOLD = 5;

const DEFAULTS = {
  perPage: 1.25,
  perPageLg: 4.23,
  loop: false,
  dots: false,
  gap: 8,
  gapLg: 12,
  edgePadding: 16,
  edgePaddingLg: 98,
  minSlidesForControls: 5,
  minSlidesForDrag: 5,
  duration: 25,
};

const TOKEN_VARS = {
  gapMobile: '--layout-standard-mobile-gutter',
  gapDesktop: '--layout-standard-desktop-gutter',
  edgeMobile: '--layout-standard-mobile-margin',
  edgeDesktop: '--layout-standard-desktop-margin',
};

let carouselId = 0;

/**
 * @param {{ slideCount: number, perPage: number, isDesktop: boolean, minSlidesForDrag: number }} options
 * @returns {boolean}
 */
export function isCarouselDragEnabled({ slideCount, perPage, isDesktop, minSlidesForDrag }) {
  return isDesktop ? slideCount >= minSlidesForDrag : slideCount > perPage;
}

/**
 * Accessible horizontal carousel using Embla for scroll physics.
 * @extends HTMLElement
 */
class CarouselElement extends HTMLElement {
  /** @type {import('./embla-carousel.esm.js').default | null} */
  #embla = null;
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement | null} */
  #viewport = null;
  /** @type {HTMLElement | null} */
  #reelRoot = null;
  /** @type {HTMLElement | null} */
  #prevWrapper = null;
  /** @type {HTMLElement | null} */
  #nextWrapper = null;
  /** @type {HTMLElement | null} */
  #liveRegion = null;
  /** @type {HTMLElement[]} */
  #realSlides = [];
  /** @type {HTMLElement | null} */
  #dotsContainer = null;
  /** @type {number} */
  #currentIndex = 0;
  /** @type {number} */
  #activePerPage = DEFAULTS.perPage;
  /** @type {number} */
  #activeGap = DEFAULTS.gap;
  /** @type {number} */
  #activeEdgePadding = DEFAULTS.edgePadding;
  /** @type {boolean} */
  #legacy = false;
  /** @type {boolean} */
  #isLoadingMore = false;
  /** @type {string} */
  #instanceId = '';
  /** @type {number} */
  #measureFrame = 0;
  /** @type {number} */
  #lastDotCount = -1;
  /** @type {boolean} */
  #eventsBound = false;

  /** @type {ResizeObserver} */
  #resizeObserver = new ResizeObserver(() => this.#scheduleMeasure());
  /** @type {MutationObserver} */
  #slideObserver = new MutationObserver(() => this.#onSlidesMutated());
  /** @type {MediaQueryList} */
  #desktopQuery = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);

  /**
   * @returns {string[]}
   */
  static get observedAttributes() {
    return ['per-page', 'per-page-lg', 'mobileslidesperview', 'loop', 'dots', 'aria-label', 'gap', 'edge-padding'];
  }

  /** @returns {number} */
  get index() {
    return this.#embla?.selectedScrollSnap() ?? this.#currentIndex;
  }

  /** @param {number} index */
  set index(index) {
    this.scrollTo(index);
  }

  /** @returns {number} */
  get slideCount() {
    return this.#realSlides.length;
  }

  /** @returns {number} */
  get #maxIndex() {
    return Math.max(0, this.#realSlides.length - Math.floor(this.#activePerPage));
  }

  /** @returns {number} */
  get maxIndex() {
    return this.#maxIndex;
  }

  /** @returns {number} */
  get #perPage() {
    const legacy = this.getAttribute('mobileslidesperview');
    if (legacy != null) return parseFloat(legacy);

    const modern = this.getAttribute('per-page');
    if (modern != null) return parseFloat(modern);

    return DEFAULTS.perPage;
  }

  /** @returns {number} */
  get #perPageLg() {
    const value = this.getAttribute('per-page-lg');
    return value == null ? DEFAULTS.perPageLg : parseFloat(value);
  }

  /** @returns {boolean} */
  get #loop() {
    return this.hasAttribute('loop');
  }

  /** @returns {number} */
  get #minSlidesForDrag() {
    const value = this.getAttribute('min-slides-drag');
    return value == null ? DEFAULTS.minSlidesForDrag : parseFloat(value);
  }

  /** @returns {boolean} */
  get #dots() {
    return this.hasAttribute('dots');
  }

  /** @returns {boolean} */
  get #isDesktop() {
    return this.#desktopQuery.matches;
  }

  /** @returns {number} */
  get #resolvedPerPage() {
    return this.#isDesktop ? this.#perPageLg : this.#perPage;
  }

  /** @returns {number} */
  get #dotCount() {
    return this.#loop ? this.#realSlides.length : this.#maxIndex + 1;
  }

  /** @returns {string} */
  get #carouselName() {
    return (
      this.getAttribute('aria-label') ??
      this.closest('section')?.querySelector('.section-heading, h2')?.textContent?.trim() ??
      'Carousel'
    );
  }

  /** @returns {boolean} */
  get #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** @returns {void} */
  #onBreakpointChange = () => {
    if (this.isConnected) this.#scheduleMeasure();
  };

  /** @returns {void} */
  connectedCallback() {
    this.#desktopQuery.addEventListener('change', this.#onBreakpointChange);

    if (this.ownerDocument.readyState === 'loading') {
      this.ownerDocument.addEventListener('DOMContentLoaded', () => this.#init(), { once: true });
    } else {
      queueMicrotask(() => this.#init());
    }
  }

  /** @returns {void} */
  disconnectedCallback() {
    this.#destroyEmbla();
    this.#resizeObserver.disconnect();
    this.#slideObserver.disconnect();
    this.#desktopQuery.removeEventListener('change', this.#onBreakpointChange);
    if (this.#measureFrame) cancelAnimationFrame(this.#measureFrame);
    this.removeEventListener('dragstart', this.#onDragStart);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('click', this.#onControlClick);
    this.#eventsBound = false;
  }

  /** @returns {void} */
  attributeChangedCallback() {
    if (this.isConnected) this.#scheduleMeasure();
  }

  /** @returns {void} */
  #onSlidesMutated() {
    const count = this.#realSlides.length;
    this.#collectSlides();

    if (!this.#track || this.#realSlides.length === 0) return;
    if (this.#realSlides.length === count) return;

    this.#scheduleMeasure();
  }

  /** @returns {void} */
  #resolveDOM() {
    this.#legacy = Boolean(this.querySelector('.reel-slider, .swiper-wrapper, .swiper-slide'));

    this.#reelRoot = this.querySelector('.reel-slider') ?? this.children[0] ?? null;

    this.#track =
      this.#reelRoot?.querySelector('.swiper-wrapper') ?? this.querySelector('[data-carousel-track]') ?? this.#reelRoot;

    this.#ensureViewport();
    this.#reelRoot?.style.removeProperty('overflow');

    this.#resolveControls();
    this.#collectSlides();
  }

  /**
   * Resolves the Embla viewport. Overflow must live here — never on `.reel-slider`,
   * or prev/next gradient controls (negative left offset) get clipped.
   * @returns {void}
   */
  #ensureViewport() {
    const existing = this.#reelRoot?.querySelector('.carousel-viewport') ?? this.querySelector('.carousel-viewport');

    if (existing instanceof HTMLElement) {
      this.#viewport = existing;
      return;
    }

    if (!this.#reelRoot || !this.#track) {
      this.#viewport = null;
      return;
    }

    if (this.#track.parentElement === this.#reelRoot) {
      const viewport = document.createElement('div');
      viewport.className = 'carousel-viewport';
      this.#reelRoot.insertBefore(viewport, this.#track);
      viewport.appendChild(this.#track);
      this.#viewport = viewport;
      console.warn(
        '[carousel-reel] Wrapped .swiper-wrapper in .carousel-viewport. ' +
          'Add <div class="carousel-viewport"> in markup to avoid DOM mutation.',
      );
      return;
    }

    if (this.#track.parentElement instanceof HTMLElement) {
      this.#viewport = this.#track.parentElement;
      return;
    }

    this.#viewport = null;
  }

  /** @returns {void} */
  #resolveControls() {
    if (!this.#reelRoot) return;

    let prevWrapper = this.#reelRoot.querySelector('.swiper-control--previous');
    let nextWrapper = this.#reelRoot.querySelector('.swiper-control--next');

    if (!prevWrapper && this.#reelRoot.children[1] instanceof HTMLElement) {
      console.warn('[carousel-reel] Using child-index fallback for previous control wrapper.');
      prevWrapper = this.#reelRoot.children[1];
    }

    if (!nextWrapper && this.#reelRoot.children[2] instanceof HTMLElement) {
      console.warn('[carousel-reel] Using child-index fallback for next control wrapper.');
      nextWrapper = this.#reelRoot.children[2];
    }

    this.#prevWrapper = prevWrapper;
    this.#nextWrapper = nextWrapper;

    const prevBtn =
      this.#resolveControlButton(prevWrapper, 'prev') ??
      this.#resolveControlButton(this.querySelector('[data-carousel-control-prev], .swiper-control--previous'), 'prev');

    const nextBtn =
      this.#resolveControlButton(nextWrapper, 'next') ??
      this.#resolveControlButton(this.querySelector('[data-carousel-control-next], .swiper-control--next'), 'next');

    prevBtn?.setAttribute('data-carousel-prev', '');
    nextBtn?.setAttribute('data-carousel-next', '');

    if (prevWrapper && !prevWrapper.hasAttribute('data-carousel-control-prev')) {
      prevWrapper.setAttribute('data-carousel-control', '');
      prevWrapper.setAttribute('data-carousel-control-prev', '');
    }

    if (nextWrapper && !nextWrapper.hasAttribute('data-carousel-control-next')) {
      nextWrapper.setAttribute('data-carousel-control', '');
      nextWrapper.setAttribute('data-carousel-control-next', '');
    }
  }

  /** @returns {void} */
  #collectSlides() {
    if (this.#legacy) {
      const root = this.#track ?? this;
      this.#realSlides = Array.from(
        root.querySelectorAll(
          '.swiper-slide[data-carousel-origin]:not(.swiper-slide--load-more), ' +
            '.swiper-slide:not(.swiper-slide--load-more):not([data-carousel-clone]):not([data-carousel-origin])',
        ),
      );
      return;
    }

    this.#realSlides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
  }

  /**
   * @param {string} name
   * @param {number} fallback
   * @returns {number}
   */
  #readTokenPx(name, fallback) {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (!raw) return fallback;
    return parseFloat(raw.replace('rem', '')) * 16;
  }

  /** @returns {number} */
  #resolvedGap() {
    if (this.hasAttribute('gap')) {
      const attrValue = parseFloat(this.getAttribute('gap'));
      if (Number.isFinite(attrValue)) return attrValue;
    }

    const token = this.#isDesktop ? TOKEN_VARS.gapDesktop : TOKEN_VARS.gapMobile;
    const fallback = this.#isDesktop ? DEFAULTS.gapLg : DEFAULTS.gap;
    const value = this.#readTokenPx(token, fallback);
    return Number.isFinite(value) ? value : fallback;
  }

  /** @returns {number} */
  #resolvedEdgePadding() {
    if (this.hasAttribute('edge-padding')) {
      const attrValue = parseFloat(this.getAttribute('edge-padding'));
      if (Number.isFinite(attrValue)) return attrValue;
    }

    const token = this.#isDesktop ? TOKEN_VARS.edgeDesktop : TOKEN_VARS.edgeMobile;
    const fallback = this.#isDesktop ? DEFAULTS.edgePaddingLg : DEFAULTS.edgePadding;
    const value = this.#readTokenPx(token, fallback);
    return Number.isFinite(value) ? value : fallback;
  }

  /** @returns {void} */
  #init() {
    this.#resolveDOM();

    if (!this.#track || !this.#viewport) {
      console.warn('[carousel-reel] No track or viewport element found.');
      return;
    }

    if (this.#realSlides.length === 0) {
      console.warn('[carousel-reel] No slides found.');
      return;
    }

    this.#instanceId = `carousel-${++carouselId}`;
    this.#markOriginSlides();
    this.#setupA11y();

    if (this.#dots) this.#setupDots();

    if (!this.#viewport.classList?.contains('carousel-viewport')) {
      console.warn(
        '[carousel-reel] Embla viewport is not .carousel-viewport — ' +
          'control gradients may be clipped. Wrap .swiper-wrapper in .carousel-viewport.',
      );
    }

    this.#scheduleMeasure();
    this.#bindEvents();
    this.#resizeObserver.observe(this);
    this.#slideObserver.observe(this.#track, { childList: true });
  }

  /** @returns {void} */
  #markOriginSlides() {
    this.#realSlides.forEach((slide) => {
      slide.setAttribute('data-carousel-origin', '');
    });
  }

  /** @returns {void} */
  #setupA11y() {
    this.setAttribute('role', 'region');
    this.setAttribute('aria-roledescription', 'carousel');

    const heading = this.closest('section')?.querySelector('.section-heading, h2');
    if (heading) {
      if (!heading.id) heading.id = `${this.#instanceId}-heading`;
      this.setAttribute('aria-labelledby', heading.id);
    } else if (!this.hasAttribute('aria-label')) {
      this.setAttribute('aria-label', 'Carousel');
    }

    if (!this.#liveRegion) {
      this.#liveRegion = document.createElement('div');
      this.#liveRegion.className = 'carousel-sr-only';
      this.#liveRegion.setAttribute('aria-live', 'polite');
      this.#liveRegion.setAttribute('aria-atomic', 'true');
      this.#liveRegion.setAttribute('role', 'status');
      this.appendChild(this.#liveRegion);
    }

    if (!this.querySelector('[data-carousel-instructions]')) {
      const instructions = document.createElement('p');
      instructions.className = 'carousel-sr-only';
      instructions.setAttribute('data-carousel-instructions', '');
      instructions.id = `${this.#instanceId}-instructions`;
      instructions.textContent =
        'Use left and right arrow keys to move between slides. Home and End go to the first and last slide.';
      this.appendChild(instructions);
      this.setAttribute('aria-describedby', instructions.id);
    }

    this.setAttribute('tabindex', '0');

    if (this.#viewport) {
      if (!this.#viewport.id) this.#viewport.id = `${this.#instanceId}-viewport`;
      this.#viewport.setAttribute('role', 'group');
      this.#viewport.setAttribute('aria-roledescription', 'slides');
      this.#viewport.setAttribute('aria-label', `${this.#carouselName} slides`);
    }

    if (!this.#legacy) this.#setupKeyboardNav();

    this.#realSlides.forEach((slide, index) => {
      if (!slide.id) slide.id = `${this.#instanceId}-slide-${index + 1}`;
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', this.#slideLabel(slide, index));
    });

    this.#enhanceControls();
    this.#preventNativeDrag();
  }

  /** @returns {void} */
  #preventNativeDrag() {
    this.querySelectorAll('img').forEach((img) => {
      img.setAttribute('draggable', 'false');
    });
  }

  /** @returns {void} */
  #setupKeyboardNav() {
    if (this.querySelector('[data-carousel-keyboard-nav]')) return;

    const nav = document.createElement('div');
    nav.className = 'carousel-keyboard-nav';
    nav.setAttribute('data-carousel-keyboard-nav', '');

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'carousel-keyboard-nav__btn';
    prev.setAttribute('data-carousel-prev', '');
    prev.textContent = 'Previous';

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'carousel-keyboard-nav__btn';
    next.setAttribute('data-carousel-next', '');
    next.textContent = 'Next';

    nav.append(prev, next);
    this.appendChild(nav);
    this.#enhanceControls();
  }

  /**
   * Resolves the prev/next button inside a control wrapper.
   * Production markup uses `button.toggleable-icon-button` without Swiper classes.
   * @param {Element | null} wrapper
   * @param {'prev' | 'next'} direction
   * @returns {HTMLButtonElement | null}
   */
  #resolveControlButton(wrapper, direction) {
    if (!(wrapper instanceof HTMLElement)) return null;

    const selectors =
      direction === 'prev'
        ? ['.swiper-button-prev', '[data-carousel-prev]', 'button.toggleable-icon-button', 'button']
        : ['.swiper-button-next', '[data-carousel-next]', 'button.toggleable-icon-button', 'button'];

    for (const selector of selectors) {
      const candidate = wrapper.querySelector(selector);
      if (candidate instanceof HTMLButtonElement) return candidate;
    }

    if (wrapper.firstElementChild instanceof HTMLButtonElement) {
      return wrapper.firstElementChild;
    }

    return null;
  }

  /**
   * @param {'prev' | 'next'} direction
   * @returns {HTMLButtonElement[]}
   */
  #controlButtons(direction) {
    /** @type {Set<HTMLButtonElement>} */
    const buttons = new Set();

    const wrappers =
      direction === 'prev'
        ? [this.#prevWrapper, ...this.querySelectorAll('[data-carousel-control-prev], .swiper-control--previous')]
        : [this.#nextWrapper, ...this.querySelectorAll('[data-carousel-control-next], .swiper-control--next')];

    for (const wrapper of wrappers) {
      const button = this.#resolveControlButton(wrapper, direction);
      if (button) buttons.add(button);
    }

    const attrSelector = direction === 'prev' ? '[data-carousel-prev]' : '[data-carousel-next]';
    const classSelector = direction === 'prev' ? '.swiper-button-prev' : '.swiper-button-next';

    this.querySelectorAll(`${attrSelector}, ${classSelector}`).forEach((element) => {
      if (element instanceof HTMLButtonElement) buttons.add(element);
    });

    return Array.from(buttons);
  }

  /** @returns {void} */
  #enhanceControls() {
    const viewportId = this.#viewport?.id ?? '';

    this.#controlButtons('prev').forEach((btn) => {
      btn.setAttribute('data-carousel-prev', '');
      if (!btn.classList.contains('carousel-keyboard-nav__btn')) {
        btn.setAttribute('aria-label', `Previous slide, ${this.#carouselName}`);
      }
      if (viewportId) btn.setAttribute('aria-controls', viewportId);
    });

    this.#controlButtons('next').forEach((btn) => {
      btn.setAttribute('data-carousel-next', '');
      if (!btn.classList.contains('carousel-keyboard-nav__btn')) {
        btn.setAttribute('aria-label', `Next slide, ${this.#carouselName}`);
      }
      if (viewportId) btn.setAttribute('aria-controls', viewportId);
    });
  }

  /**
   * @param {HTMLElement} slide
   * @param {number} index
   * @returns {string}
   */
  #slideLabel(slide, index) {
    const cardName = slide.querySelector('.card-name, .c-product-tile__product-name');
    if (cardName?.textContent?.trim()) return cardName.textContent.trim();

    const eyebrow = slide.querySelector('.tile-eyebrow, .category-tile__eyebrow');
    const title = slide.querySelector('.tile-label, .category-tile__title');
    if (title?.textContent?.trim()) {
      if (eyebrow?.textContent?.trim()) {
        return `${eyebrow.textContent.trim()}, ${title.textContent.trim()}`;
      }
      return title.textContent.trim();
    }

    const img = slide.querySelector('img[alt]');
    if (img?.alt) return img.alt;

    return `Slide ${index + 1}`;
  }

  /** @returns {void} */
  #scheduleMeasure() {
    if (this.#measureFrame) cancelAnimationFrame(this.#measureFrame);

    this.#measureFrame = requestAnimationFrame(() => {
      this.#measureFrame = 0;
      this.#measure();
    });
  }

  /**
   * @returns {boolean}
   */
  #hasValidLayout() {
    if (!this.#track || !this.#viewport || this.#realSlides.length === 0) return false;

    const trackWidth = this.#viewport.offsetWidth;
    const perPage = this.#resolvedPerPage;
    const gap = this.#resolvedGap();
    const slideWidth = (trackWidth - gap * (perPage - 1)) / perPage;

    return trackWidth > 0 && Number.isFinite(slideWidth) && slideWidth > 0;
  }

  /** @returns {void} */
  #measure() {
    if (!this.#track || !this.#viewport) return;

    this.#collectSlides();
    if (this.#realSlides.length === 0) return;

    this.#markOriginSlides();
    this.#activePerPage = this.#resolvedPerPage;
    this.#activeGap = this.#resolvedGap();
    this.#activeEdgePadding = this.#resolvedEdgePadding();

    const measureEl = this.#viewport;
    const trackWidth = measureEl.offsetWidth;
    const slideWidth = (trackWidth - this.#activeGap * (this.#activePerPage - 1)) / this.#activePerPage;

    if (trackWidth <= 0 || !Number.isFinite(slideWidth) || slideWidth <= 0) {
      this.#destroyEmbla();
      return;
    }

    this.style.setProperty('--carousel-gap', `${this.#activeGap}px`);
    this.style.setProperty('--carousel-edge-padding', `${this.#activeEdgePadding}px`);

    this.#track.style.gap = '0';

    this.#realSlides.forEach((slide, index) => {
      if (!this.#track?.contains(slide)) {
        console.warn('[carousel-reel] Slide is not inside the track element.', slide);
      }

      slide.style.width = `${slideWidth}px`;
      slide.style.flexShrink = '0';
      const isLast = index === this.#realSlides.length - 1;
      slide.style.marginRight = !this.#loop && isLast ? '0' : `${this.#activeGap}px`;
    });

    const loadMoreSlide = this.#track.querySelector('.swiper-slide--load-more');
    if (loadMoreSlide instanceof HTMLElement) {
      loadMoreSlide.style.width = '0';
      loadMoreSlide.style.marginRight = '0';
      loadMoreSlide.style.flexShrink = '0';
      loadMoreSlide.style.overflow = 'hidden';
    }

    const savedIndex = this.#embla?.selectedScrollSnap() ?? this.#currentIndex;

    if (!this.#activateEmbla()) return;

    this.#scrollToIndex(Math.min(savedIndex, this.#maxIndex), false, false);
    this.#onEmblaSelect();

    const dotCount = this.#dotCount;
    if (dotCount !== this.#lastDotCount) {
      this.#rebuildDots();
      this.#lastDotCount = dotCount;
    } else {
      this.#updateDots();
    }

    this.toggleAttribute('data-has-controls', this.#realSlides.length >= DEFAULTS.minSlidesForControls);
    this.toggleAttribute('data-draggable', this.#dragEnabled);
    this.#syncControlHeight();
    this.#updateControls();
    this.#updateSlideVisibility();
    this.#preventNativeDrag();
  }

  /** @returns {void} */
  #syncControlHeight() {
    const slide = this.#realSlides[0];
    const imageEl = slide?.querySelector('.c-product-tile__image, .card-img, .tile-img, .slide-link__content-wrapper');
    const height = imageEl?.offsetHeight ?? this.#viewport?.offsetHeight ?? 0;
    if (height > 0) {
      this.style.setProperty('--carousel-control-height', `${height}px`);
    }
  }

  /** @returns {boolean} */
  get #dragEnabled() {
    return isCarouselDragEnabled({
      slideCount: this.#realSlides.length,
      perPage: this.#activePerPage,
      isDesktop: this.#isDesktop,
      minSlidesForDrag: this.#minSlidesForDrag,
    });
  }

  /** @returns {Record<string, unknown>} */
  #emblaOptions() {
    const slides = this.#realSlides.filter((slide) => this.#track?.contains(slide));

    return {
      container: this.#track,
      slides,
      loop: this.#loop,
      align: 'start',
      containScroll: false,
      slidesToScroll: 1,
      dragThreshold: DRAG_CLICK_THRESHOLD,
      duration: DEFAULTS.duration,
      skipSnaps: false,
      watchSlides: false,
      watchDrag: this.#dragEnabled,
    };
  }

  /**
   * Creates or re-initialises Embla when layout is valid.
   * @returns {boolean}
   */
  #activateEmbla() {
    if (!this.#viewport || !this.#track || !this.#hasValidLayout()) {
      this.#destroyEmbla();
      return false;
    }

    this.#slideObserver.disconnect();

    if (this.#embla) {
      this.#embla.reInit(this.#emblaOptions());
    } else {
      this.#destroyEmbla();
      this.#embla = EmblaCarousel(this.#viewport, this.#emblaOptions());
      this.#embla.on('select', this.#onEmblaSelect);
      this.#embla.on('settle', this.#onEmblaSelect);
      this.#embla.on('scroll', this.#onEmblaScroll);
      this.#embla.on('reInit', this.#onEmblaSelect);
    }

    if (this.#embla.scrollSnapList().length === 0) {
      console.warn(
        '[carousel-reel] Embla initialised with zero scroll snaps. ' +
          'Check that .swiper-wrapper contains .swiper-slide elements and the viewport has width.',
      );
      this.#destroyEmbla();
      return false;
    }

    if (this.#track) {
      queueMicrotask(() => {
        if (this.isConnected && this.#track) {
          this.#slideObserver.observe(this.#track, { childList: true });
        }
      });
    }

    return true;
  }

  /** @returns {void} */
  #destroyEmbla() {
    this.#embla?.destroy();
    this.#embla = null;
  }

  /** @returns {void} */
  #onEmblaSelect = () => {
    if (!this.#embla) return;

    const index = this.#normalizeIndex(this.#embla.selectedScrollSnap());
    const previousIndex = this.#currentIndex;
    this.#currentIndex = index;

    this.#updateDots();
    this.#updateControls();
    this.#updateSlideVisibility();

    if (previousIndex !== index) {
      this.#announcePosition();
      this.#maybeLoadMore();
      this.#dispatch('carousel:change', { index });
    }
  };

  /** @returns {void} */
  #onEmblaScroll = () => {
    this.#updateSlideVisibility();
  };

  /**
   * @param {number} snapIndex
   * @returns {number}
   */
  #normalizeIndex(snapIndex) {
    const count = this.#realSlides.length;
    if (count === 0) return 0;

    if (this.#loop) {
      return ((snapIndex % count) + count) % count;
    }

    return Math.max(0, Math.min(snapIndex, this.#maxIndex));
  }

  /**
   * @returns {{ first: number, last: number }}
   */
  #visibleRange() {
    const visibleCount = Math.max(1, Math.ceil(this.#activePerPage));
    const first = Math.max(0, Math.min(this.#currentIndex, this.#maxIndex));
    const last = Math.min(this.#realSlides.length - 1, first + visibleCount - 1);
    return { first, last };
  }

  /** @returns {void} */
  #updateSlideVisibility() {
    const { first, last } = this.#visibleRange();

    this.#realSlides.forEach((slide, index) => {
      const hidden = index < first || index > last;
      slide.setAttribute('aria-hidden', `${hidden}`);
      slide.toggleAttribute('inert', hidden);
    });
  }

  /** @returns {void} */
  #announcePosition() {
    if (!this.#liveRegion) return;

    const slide = this.#realSlides[this.#currentIndex];
    const label = slide ? this.#slideLabel(slide, this.#currentIndex) : '';
    const position = this.#loop
      ? `${this.#currentIndex + 1} of ${this.#realSlides.length}`
      : `${this.#currentIndex + 1} of ${this.#maxIndex + 1}`;

    this.#liveRegion.textContent = `${label}. Slide ${position}.`;
  }

  /**
   * @param {number} index
   * @param {boolean} [animated=true]
   * @param {boolean} [announce=true]
   * @returns {void}
   */
  #scrollToIndex(index, animated = true, announce = true) {
    if (!this.#embla || this.#embla.scrollSnapList().length === 0) return;

    const target = this.#loop
      ? ((index % this.#realSlides.length) + this.#realSlides.length) % this.#realSlides.length
      : Math.max(0, Math.min(index, this.#maxIndex));

    const instant = !animated || this.#prefersReducedMotion;
    this.#embla.scrollTo(target, instant);

    if (announce) {
      queueMicrotask(() => this.#onEmblaSelect());
    }
  }

  /** @returns {void} */
  next() {
    if (!this.#embla || this.#embla.scrollSnapList().length === 0) return;
    this.#scrollToIndex(this.#currentIndex + 1);
  }

  /** @returns {void} */
  prev() {
    if (!this.#embla || this.#embla.scrollSnapList().length === 0) return;
    this.#scrollToIndex(this.#currentIndex - 1);
  }

  /**
   * @param {number} index
   * @param {{ animated?: boolean }} [options]
   * @returns {void}
   */
  scrollTo(index, options = {}) {
    const { animated = true } = options;
    this.#scrollToIndex(index, animated);
  }

  /** @returns {void} */
  #maybeLoadMore() {
    if (this.#realSlides.length - this.#currentIndex <= Math.ceil(this.#activePerPage) + LOAD_MORE_THRESHOLD) {
      this.#loadMoreProducts();
    }
  }

  /** @returns {Promise<void>} */
  async #loadMoreProducts() {
    if (this.#isLoadingMore || !this.#track) return;

    const loadMoreSlide = this.querySelector('.swiper-slide--load-more');
    if (!loadMoreSlide) return;

    const nextUrl = loadMoreSlide.getAttribute('data-next-url');
    if (!nextUrl) return;

    this.#isLoadingMore = true;

    try {
      const response = await fetch(nextUrl);
      if (!response.ok) throw new Error('Network response was not ok');

      const htmlText = await response.text();
      const doc = new DOMParser().parseFromString(htmlText, 'text/html');
      const newCarousel = doc.querySelector('carousel-reel');
      if (!newCarousel) return;

      const newSlides = Array.from(newCarousel.querySelectorAll('.swiper-slide.product-tile'));
      const newLoadMoreSlide = newCarousel.querySelector('.swiper-slide--load-more');

      loadMoreSlide.remove();
      newSlides.forEach((slide) => {
        slide.setAttribute('data-carousel-origin', '');
        this.#track.appendChild(slide);
      });

      if (newLoadMoreSlide) {
        this.#track.appendChild(newLoadMoreSlide);
      }

      this.#collectSlides();
      this.#scheduleMeasure();
    } catch (error) {
      console.error('Failed to load more products:', error);
    } finally {
      this.#isLoadingMore = false;
    }
  }

  /** @returns {void} */
  #setupDots() {
    this.#dotsContainer = this.querySelector('[data-carousel-dots]');

    if (!this.#dotsContainer) {
      this.#dotsContainer = document.createElement('div');
      this.#dotsContainer.setAttribute('data-carousel-dots', '');
      this.appendChild(this.#dotsContainer);
    }

    this.#dotsContainer.setAttribute('role', 'tablist');
    this.#dotsContainer.setAttribute('aria-label', `${this.#carouselName} pages`);
    this.#rebuildDots();
  }

  /** @returns {void} */
  #rebuildDots() {
    if (!this.#dotsContainer) return;

    this.#dotsContainer.replaceChildren();

    for (let i = 0; i < this.#dotCount; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Slide ${i + 1} of ${this.#dotCount}`);
      dot.setAttribute('data-index', `${i}`);

      const slide = this.#realSlides[i];
      if (slide?.id) dot.setAttribute('aria-controls', slide.id);

      dot.addEventListener('click', () => {
        this.#scrollToIndex(i);
        dot.focus();
      });

      dot.addEventListener('keydown', (event) => this.#onDotKeyDown(event, i));
      this.#dotsContainer.appendChild(dot);
    }

    this.#updateDots();
  }

  /** @returns {void} */
  #updateDots() {
    if (!this.#dotsContainer) return;

    this.#dotsContainer.querySelectorAll('[role="tab"]').forEach((dot, index) => {
      const isActive = index === this.#currentIndex;
      dot.setAttribute('aria-selected', `${isActive}`);
      dot.setAttribute('tabindex', isActive ? '0' : '-1');
      dot.toggleAttribute('data-active', isActive);
    });
  }

  /** @returns {void} */
  #updateControls() {
    const hasEnoughSlides = this.#realSlides.length >= DEFAULTS.minSlidesForControls;
    const atStart = this.#currentIndex === 0;
    const atEnd = this.#currentIndex >= this.#maxIndex;

    const hidePrev = !hasEnoughSlides || (atStart && !this.#loop);
    const hideNext = !hasEnoughSlides || (atEnd && !this.#loop);

    this.#controlButtons('prev').forEach((button) => {
      button.disabled = hidePrev;
      button.setAttribute('aria-disabled', `${hidePrev}`);
      button.tabIndex = hidePrev ? -1 : 0;
      button.classList.toggle('swiper-button-hidden', hidePrev);
      button.toggleAttribute('aria-hidden', hidePrev);
    });

    this.#controlButtons('next').forEach((button) => {
      button.disabled = hideNext;
      button.setAttribute('aria-disabled', `${hideNext}`);
      button.tabIndex = hideNext ? -1 : 0;
      button.classList.toggle('swiper-button-hidden', hideNext);
      button.toggleAttribute('aria-hidden', hideNext);
    });

    this.#updateLegacyClasses(hidePrev, hideNext, hasEnoughSlides);
  }

  /**
   * Toggles prev/next wrapper visibility (gradients live on these wrappers).
   * @param {'prev' | 'next'} direction
   * @param {boolean} hide
   * @returns {void}
   */
  #setControlWrapperVisible(direction, hide) {
    const selector =
      direction === 'prev'
        ? '.swiper-control--previous, [data-carousel-control-prev]'
        : '.swiper-control--next, [data-carousel-control-next]';

    this.querySelectorAll(selector).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      node.classList.toggle('swiper-control--hidden', hide);
      node.toggleAttribute('data-hidden', hide);
    });
  }

  /**
   * @param {boolean} hidePrev
   * @param {boolean} hideNext
   * @param {boolean} hasEnoughSlides
   * @returns {void}
   */
  #updateLegacyClasses(hidePrev, hideNext, hasEnoughSlides) {
    if (!this.#legacy || !this.#track) return;

    // An explicit edge-padding attribute means the instance wants direct control over its
    // spacing, so skip the automatic extra right margin this class otherwise adds.
    this.#track.classList.toggle('right-spacing', hideNext && !this.hasAttribute('edge-padding'));
    this.#setControlWrapperVisible('prev', hidePrev);
    this.#setControlWrapperVisible('next', hideNext);

    if (!hasEnoughSlides) return;

    this.#resolveControlButton(this.#prevWrapper, 'prev')?.classList.toggle('swiper-button-hidden', hidePrev);

    this.#resolveControlButton(this.#nextWrapper, 'next')?.classList.toggle('swiper-button-hidden', hideNext);
  }

  /**
   * @template T
   * @param {string} name
   * @param {T} detail
   * @returns {void}
   */
  #dispatch(name, detail) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  /** @returns {void} */
  #bindEvents() {
    if (this.#eventsBound) return;
    this.#eventsBound = true;

    this.addEventListener('dragstart', this.#onDragStart);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('click', this.#onControlClick);
  }

  /** @param {MouseEvent} event */
  #onControlClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;
    if (target.closest('[data-carousel-dots]')) return;

    const prevControl = target.closest(
      '[data-carousel-control-prev], .swiper-control--previous, [data-carousel-prev], .swiper-button-prev',
    );
    if (prevControl) {
      const button =
        target.closest('button') ??
        this.#resolveControlButton(prevControl instanceof HTMLElement ? prevControl : null, 'prev');
      if (button?.disabled || button?.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      this.prev();
      return;
    }

    const nextControl = target.closest(
      '[data-carousel-control-next], .swiper-control--next, [data-carousel-next], .swiper-button-next',
    );
    if (nextControl) {
      const button =
        target.closest('button') ??
        this.#resolveControlButton(nextControl instanceof HTMLElement ? nextControl : null, 'next');
      if (button?.disabled || button?.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      this.next();
    }
  };

  /** @param {DragEvent} event */
  #onDragStart = (event) => {
    event.preventDefault();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    const target = event.target;
    if (target !== this && !this.contains(target)) return;
    if (target.closest?.('[data-carousel-dots]')) return;

    const { key } = event;

    if (key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
      return;
    }

    if (key === 'ArrowRight') {
      event.preventDefault();
      this.next();
      return;
    }

    if (key === 'Home') {
      event.preventDefault();
      this.#scrollToIndex(0);
      return;
    }

    if (key === 'End') {
      event.preventDefault();
      this.#scrollToIndex(this.#maxIndex);
    }
  };

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   * @returns {void}
   */
  #onDotKeyDown(event, index) {
    if (!this.#dotsContainer) return;

    const tabs = Array.from(this.#dotsContainer.querySelectorAll('[role="tab"]'));
    let targetIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        targetIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        targetIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        event.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        targetIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    this.#scrollToIndex(targetIndex);
    tabs[targetIndex]?.focus();
  }
}

if (!customElements.get('carousel-reel')) {
  customElements.define('carousel-reel', CarouselElement);
}